'use client';

/**
 * MotionReveal — Framer Motion wrapper for scroll-into-view reveals.
 * Respects `prefers-reduced-motion: reduce` by rendering a plain div.
 *
 * @spec /doc/specs/frontend/design-system/design-tokens-v2.md
 * @userStory US-EP022-011
 */

import { useEffect, useState, type ReactNode } from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useIsCrawler } from './crawler-context';

type Direction = 'up' | 'left' | 'right' | 'fade';

const VARIANTS: Record<Direction, Variants> = {
  up: { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } },
  left: { hidden: { opacity: 0, x: -24 }, visible: { opacity: 1, x: 0 } },
  right: { hidden: { opacity: 0, x: 24 }, visible: { opacity: 1, x: 0 } },
  fade: { hidden: { opacity: 0 }, visible: { opacity: 1 } },
};

export interface MotionRevealProps {
  children: ReactNode;
  direction?: Direction;
  delay?: number;
  once?: boolean;
  className?: string;
}

export function MotionReveal({
  children,
  direction = 'up',
  delay = 0,
  once = true,
  className,
}: MotionRevealProps) {
  const reduce = useReducedMotion();
  const isCrawler = useIsCrawler();
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  // SSR, reduced-motion users, or server-detected crawlers: render
  // statically without the motion wrapper. Critical for SEO — Google's WRS
  // executes JS but does NOT scroll, so whileInView never triggers and
  // motion.div stays at opacity:0 → invisible content in the screenshot.
  if (!hydrated || reduce || isCrawler) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={cn(className)}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: 0.2 }}
      transition={{
        duration: 0.6,
        delay,
        ease: [0.22, 0.61, 0.36, 1],
      }}
      variants={VARIANTS[direction]}
    >
      {children}
    </motion.div>
  );
}
